import type { Place, SatellitePlace } from '@types';
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ButtonStyled from '../shared/button/Button';
import styles from './rerouteModal.module.scss';

type RerouteModalProps = {
  rerouteDestination: Place | SatellitePlace | null;
  originalDestination: Place | SatellitePlace | null;
  onSubmit: (place?: Place | SatellitePlace | null) => void;
  isOpen: boolean;
  onContinue: (place?: Place | SatellitePlace | null) => void;
};

const RerouteModal: React.FC<RerouteModalProps> = ({
  isOpen,
  onSubmit,
  onContinue,
  rerouteDestination,
  originalDestination,
}) => {
  const { t } = useTranslation('common');

  // Determine if it's a mandatory reroute with no message
  const isMandatoryRerouteWithNoMessage = useMemo(() => {
    const navInstruction = originalDestination?.nav_instruction;
    return (
      navInstruction?.accept_only === false &&
      navInstruction?.redirect_place_id &&
      !navInstruction?.message &&
      !originalDestination?.disclaimer_text
    );
  }, [originalDestination]);

  // Determine if it's a mandatory reroute
  const isMandatoryReroute = useMemo(() => {
    const navInstruction = originalDestination?.nav_instruction;
    return (
      navInstruction?.accept_only === true &&
      navInstruction?.redirect_place_id &&
      navInstruction?.message
    );
  }, [originalDestination]);

  // Determine if it's an optional reroute
  const isOptionalReroute = useMemo(() => {
    const navInstruction = originalDestination?.nav_instruction;
    return (
      navInstruction?.accept_only === false &&
      navInstruction?.redirect_place_id &&
      navInstruction?.message
    );
  }, [originalDestination]);

  const isInformational = useMemo(
    () =>
      (!originalDestination?.nav_instruction &&
        originalDestination?.disclaimer_text) ||
      (originalDestination?.nav_instruction &&
        !originalDestination?.nav_instruction?.redirect_place_id),
    [originalDestination],
  );

  // Return null if conditions are not met to display the modal
  if (!originalDestination || !isOpen || isMandatoryRerouteWithNoMessage) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.modalOverlay}>
        <div className={styles.modal}>
          {!isInformational && (
            <div className={styles.modalImageContainer}>
              <img
                src={rerouteDestination?.kiosk_image_url}
                alt="Modal"
                className={styles.modalImage}
              />
            </div>
          )}
          <div className={styles.modalContent}>
            <h2 className={styles.title}>
              {t('campusNavigation.reroute.title')}
            </h2>
            {originalDestination.nav_instruction?.message && (
              <p style={{ marginBottom: 24 }}>
                {originalDestination.nav_instruction.message}
              </p>
            )}
            {originalDestination.disclaimer_text && (
              <p style={{ marginBottom: 24 }}>
                {originalDestination.disclaimer_text}
              </p>
            )}
            <div className={styles.buttonContainer}>
              {isOptionalReroute && (
                <>
                  <ButtonStyled
                    onClick={() => onSubmit(rerouteDestination)}
                    className={styles.button}
                    uppercase
                  >
                    {t('campusNavigation.reroute.change')}
                  </ButtonStyled>
                  <ButtonStyled
                    onClick={() => onContinue(originalDestination)}
                    className={styles.button}
                    uppercase
                  >
                    {t('campusNavigation.reroute.continue')}
                  </ButtonStyled>
                </>
              )}
              {isMandatoryReroute && (
                <ButtonStyled
                  onClick={() => onSubmit(rerouteDestination)}
                  className={styles.button}
                  uppercase
                >
                  {t('campusNavigation.reroute.confirm')}
                </ButtonStyled>
              )}
              {isInformational && (
                <ButtonStyled
                  onClick={() => onContinue(originalDestination)}
                  className={styles.button}
                  uppercase
                >
                  {t('campusNavigation.reroute.confirm')}
                </ButtonStyled>
              )}
              {!isOptionalReroute &&
                !isMandatoryReroute &&
                !isInformational && (
                  <ButtonStyled
                    onClick={() => onContinue(originalDestination)}
                    className={styles.button}
                    uppercase
                  >
                    {t('campusNavigation.reroute.confirm')}
                  </ButtonStyled>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(RerouteModal);
